import React, { useEffect } from 'react';
import { format } from 'date-fns';

function formatDiff(value) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  if (n === 0) return '0';
  return n > 0 ? `+${n}` : String(n);
}

function stopName(stops, id) {
  if (!id || !Array.isArray(stops)) return 'Stop';
  const match = stops.find((s) => String(s._id) === String(id));
  return match?.name || 'Stop';
}

function DetailRow({ label, value }) {
  return (
    <div className="detail-row">
      <div className="detail-label">{label}</div>
      <div className="detail-value">{value ?? '—'}</div>
    </div>
  );
}

export default function TripDetailDialog({ trip, onClose }) {
  useEffect(() => {
    if (!trip) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [trip, onClose]);

  if (!trip) return null;

  const routeLabel = trip.stops?.length
    ? trip.stops.map((s) => s.name).join(' → ')
    : '—';

  return (
    <div className="modal-overlay" onClick={onClose} role="presentation">
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="trip-detail-title"
      >
        <div className="modal-header">
          <div>
            <h3 id="trip-detail-title">{trip.vehicle?.name || 'Trip details'}</h3>
            <p className="text-muted text-sm" style={{ margin: '4px 0 0' }}>
              {trip.vehicle?.plate ? `${trip.vehicle.plate} · ` : ''}
              {format(new Date(trip.date), 'dd MMM yyyy')}
            </p>
          </div>
          <div className="modal-header-actions">
            <span className={`badge ${trip.status}`}>{trip.status}</span>
            <button type="button" className="btn btn-sm" onClick={onClose} aria-label="Close">
              Close
            </button>
          </div>
        </div>

        <div className="detail-grid">
          <DetailRow label="Driver" value={trip.driver?.name} />
          <DetailRow label="Purpose" value={trip.purpose || '—'} />
          <DetailRow label="Out time" value={trip.outTime || '—'} />
          <DetailRow label="In time" value={trip.inTime || '—'} />
          <DetailRow label="Meter out" value={trip.meterOut ?? '—'} />
          <DetailRow label="Meter in" value={trip.meterIn ?? '—'} />
          <DetailRow label="Expected km" value={trip.expectedKM ?? '—'} />
          <DetailRow label="Actual km" value={trip.actualKM ?? '—'} />
          <DetailRow label="Difference km" value={formatDiff(trip.difference)} />
          <DetailRow
            label="% over"
            value={trip.percentOver != null ? `${trip.percentOver}%` : '—'}
          />
          {trip.fuelPricePerLiter != null && (
            <DetailRow label="Fuel price / L" value={trip.fuelPricePerLiter} />
          )}
        </div>

        <div className="modal-section">
          <div className="card-title">Route entered by staff</div>
          <p style={{ margin: 0 }}>{routeLabel}</p>
          {trip.stops?.length > 0 && (
            <ul className="detail-stop-list">
              {trip.stops.map((stop, index) => (
                <li key={stop._id || index}>
                  <strong>{index + 1}. {stop.name}</strong>
                  {stop.fullAddress ? (
                    <span className="text-muted text-sm"> — {stop.fullAddress}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>

        {trip.driverStatement ? (
          <div className="modal-section">
            <div className="card-title">Driver statement</div>
            <p style={{ margin: 0 }}>{trip.driverStatement}</p>
          </div>
        ) : null}

        {Array.isArray(trip.routeChanges) && trip.routeChanges.length > 0 ? (
          <div className="modal-section">
            <div className="card-title">Route changes</div>
            <div className="detail-change-list">
              {trip.routeChanges.map((change, index) => (
                <div key={index} className="detail-change-item">
                  <div className="text-sm">
                    <strong>{change.recordedBy || 'Staff'}</strong>
                    {change.addedAt ? (
                      <span className="text-muted">
                        {' '}
                        · {format(new Date(change.addedAt), 'dd MMM yyyy HH:mm')}
                      </span>
                    ) : null}
                  </div>
                  {Array.isArray(change.addedStops) && change.addedStops.length > 0 ? (
                    <div className="text-sm mt8">
                      Added: {change.addedStops.map((id) => stopName(trip.stops, id)).join(', ')}
                    </div>
                  ) : null}
                  {change.note ? (
                    <div className="text-sm mt8">{change.note}</div>
                  ) : null}
                  <div className="text-muted text-sm mt8">
                    Expected km: {change.previousExpectedKM ?? '—'} → {change.newExpectedKM ?? '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="text-muted text-sm" style={{ marginTop: 16 }}>
          Logged {trip.createdAt ? format(new Date(trip.createdAt), 'dd MMM yyyy HH:mm') : '—'}
          {trip.updatedAt && trip.updatedAt !== trip.createdAt
            ? ` · updated ${format(new Date(trip.updatedAt), 'dd MMM yyyy HH:mm')}`
            : ''}
        </div>
      </div>
    </div>
  );
}
